import React, { useState } from 'react';

export function ContactForm({ initialService = "", className = "" }) {
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    ydelse: initialService || "Komplet Fodbehandling (499 kr.)",
    beskrivelse: "",
    website_url: "" // Honeypot antispam field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) {
      errs.navn = "Angiv venligst dit navn";
    }
    if (!formData.telefon.trim()) {
      errs.telefon = "Angiv venligst et gyldigt telefonnummer";
    } else if (formData.telefon.replace(/[^0-9+]/g, '').length < 8) {
      errs.telefon = "Telefonnummeret skal have mindst 8 cifre";
    }
    if (!formData.email.trim()) {
      errs.email = "Angiv venligst din emailadresse";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = "Ugyldigt emailformat";
    }
    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = "Skriv venligst en kort besked eller ønske til tid/behandling";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot bot check
    if (formData.website_url) {
      console.warn("Spam detected via honeypot");
      setStatus('success');
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          ydelse: formData.ydelse,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          navn: "",
          telefon: "",
          email: "",
          ydelse: "Komplet Fodbehandling (499 kr.)",
          beskrivelse: "",
          website_url: ""
        });
      } else {
        const resData = await response.json().catch(() => ({}));
        throw new Error(resData.message || "Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring på 82 82 10 10.");
      }
    } catch (err) {
      console.error("Webform submit error:", err);
      setStatus('error');
      setErrorMessage(err.message || "Kunne ikke sende beskeden lige nu. Prøv venligst igen.");
    }
  };

  return (
    <div className={`bg-canvas rounded-3xl p-6 sm:p-10 border border-brand-border shadow-xs ${className}`}>
      {status === 'success' ? (
        <div className="text-center py-10 space-y-4 animate-in fade-in zoom-in-95 duration-300">
          <div className="w-16 h-16 rounded-full bg-surface border border-brand-border text-brand-accent flex items-center justify-center text-3xl mx-auto shadow-xs">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h3 className="text-2xl font-serif font-bold text-brand-charcoal">
            Tak for din henvendelse!
          </h3>
          <p className="text-brand-muted text-sm max-w-md mx-auto leading-relaxed">
            Vi har modtaget din besked og kontakter dig hurtigst muligt (typisk indenfor få timer på hverdage) for at bekræfte din aftale.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-brand-charcoal bg-surface hover:bg-surface-soft border border-brand-border px-5 py-2.5 rounded-full transition-colors mt-4"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            <span>Send en ny besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Honeypot field (Antispam) strictly hidden */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave empty)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="form-navn" className="block text-xs font-bold text-brand-charcoal uppercase tracking-wider mb-1.5">
                Dit Navn <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="form-navn"
                name="navn"
                value={formData.navn}
                onChange={(e) => setFormData({ ...formData, navn: e.target.value })}
                placeholder="F.eks. Karen Jensen"
                className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
                  errors.navn 
                    ? 'border-red-400 bg-red-50/40 focus:ring-red-300' 
                    : 'border-brand-border bg-surface/40 hover:bg-canvas focus:bg-canvas focus:ring-brand-accent focus:border-brand-accent text-brand-charcoal'
                }`}
                required
              />
              {errors.navn && (
                <p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                  {errors.navn}
                </p>
              )}
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor="form-telefon" className="block text-xs font-bold text-brand-charcoal uppercase tracking-wider mb-1.5">
                Telefonnummer <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                id="form-telefon"
                name="telefon"
                value={formData.telefon}
                onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                placeholder="F.eks. 82 82 10 10"
                className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
                  errors.telefon 
                    ? 'border-red-400 bg-red-50/40 focus:ring-red-300' 
                    : 'border-brand-border bg-surface/40 hover:bg-canvas focus:bg-canvas focus:ring-brand-accent focus:border-brand-accent text-brand-charcoal'
                }`}
                required
              />
              {errors.telefon && (
                <p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                  {errors.telefon}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Email */}
            <div>
              <label htmlFor="form-email" className="block text-xs font-bold text-brand-charcoal uppercase tracking-wider mb-1.5">
                Emailadresse <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="form-email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="karen@eksempel.dk"
                className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
                  errors.email 
                    ? 'border-red-400 bg-red-50/40 focus:ring-red-300' 
                    : 'border-brand-border bg-surface/40 hover:bg-canvas focus:bg-canvas focus:ring-brand-accent focus:border-brand-accent text-brand-charcoal'
                }`}
                required
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Ydelse Type */}
            <div>
              <label htmlFor="form-ydelse" className="block text-xs font-bold text-brand-charcoal uppercase tracking-wider mb-1.5">
                Ønsket ydelse
              </label>
              <select
                id="form-ydelse"
                name="ydelse"
                value={formData.ydelse}
                onChange={(e) => setFormData({ ...formData, ydelse: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-brand-border bg-surface/40 hover:bg-canvas focus:bg-canvas focus:ring-brand-accent focus:border-brand-accent text-brand-charcoal text-sm transition-all"
              >
                <option value="Komplet Fodbehandling (499 kr.)">Komplet Fodbehandling (499 kr.)</option>
                <option value="Fodpleje Abonnement (119 kr./mdr.)">Fodpleje Abonnement (119 kr./mdr.)</option>
                <option value="Laserbehandling af fødder">Laserbehandling af fødder</option>
                <option value="Bestilling af Urtecremer">Bestilling af Urtecremer</option>
                <option value="Generel forespørgsel / rådgivning">Generel forespørgsel / rådgivning</option>
              </select>
            </div>
          </div>

          {/* Beskrivelse / Ønske */}
          <div>
            <label htmlFor="form-beskrivelse" className="block text-xs font-bold text-brand-charcoal uppercase tracking-wider mb-1.5">
              Besked, adresse eller ønsket tidspunkt <span className="text-red-600">*</span>
            </label>
            <textarea
              id="form-beskrivelse"
              name="beskrivelse"
              rows={4}
              value={formData.beskrivelse}
              onChange={(e) => setFormData({ ...formData, beskrivelse: e.target.value })}
              placeholder="Skriv f.eks. din adresse, eventuelle særlige fodproblemer (nedgroede negle, ligtorne mv.) og om der er bestemte ugedage der passer dig bedst..."
              className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
                errors.beskrivelse 
                  ? 'border-red-400 bg-red-50/40 focus:ring-red-300' 
                  : 'border-brand-border bg-surface/40 hover:bg-canvas focus:bg-canvas focus:ring-brand-accent focus:border-brand-accent text-brand-charcoal'
              }`}
              required
            />
            {errors.beskrivelse && (
              <p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                {errors.beskrivelse}
              </p>
            )}
          </div>

          {status === 'error' && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium flex items-start gap-2">
              <i className="fa-solid fa-triangle-exclamation text-red-500 mt-0.5" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold py-4 px-8 rounded-full text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all duration-200 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-sm" aria-hidden="true" />
                  <span>Sender forespørgsel...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-calendar-check text-sm" aria-hidden="true" />
                  <span>Send forespørgsel på hjemmebesøg</span>
                </>
              )}
            </button>
          </div>

          <p className="text-[11px] text-center text-brand-muted">
            Vi behandler dine oplysninger fortroligt og kontakter dig for at bekræfte din aftale.
          </p>
        </form>
      )}
    </div>
  );
}
